import type { WidgetConfig } from "./types";

/**
 * Feedback icon SVG
 */
const FEEDBACK_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M8 13.5H16M8 8.5H12" />
    <path d="M6.09881 19C4.7987 18.8721 3.82475 18.4816 3.17157 17.8284C2 16.6569 2 14.7712 2 11V10.5C2 6.72876 2 4.84315 3.17157 3.67157C4.34315 2.5 6.22876 2.5 10 2.5H14C17.7712 2.5 19.6569 2.5 20.8284 3.67157C22 4.84315 22 6.72876 22 10.5V11C22 14.7712 22 16.6569 20.8284 17.8284C19.6569 19 17.7712 19 14 19C13.4395 19.0125 12.9931 19.0551 12.5546 19.155C11.3562 19.4309 10.2465 20.0441 9.14987 20.5789C7.58729 21.3408 6.806 21.7218 6.31569 21.3651C5.37769 20.6665 6.29454 18.5019 6.5 17.5" />
</svg>
`;

/**
 * Close icon SVG
 */
const CLOSE_ICON = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M18 6L6.00081 17.9992M17.9992 18L6 6.00085" />
</svg>
`;

export interface LauncherState {
	button: HTMLButtonElement;
	isOpen: boolean;
}

/**
 * Create the launcher button
 */
export function createLauncher(
	config: WidgetConfig,
	onClick: () => void,
): LauncherState {
	const button = document.createElement("button");
	button.id = "fdback-widget-launcher";
	button.setAttribute("aria-label", "Open feedback widget");
	button.setAttribute("type", "button");

	// Apply base styles individually (for test compatibility with JSDOM)
	button.style.position = "fixed";
	button.style.zIndex = "2147483647";
	button.style.width = "56px";
	button.style.height = "56px";
	button.style.borderRadius = "50%";
	button.style.border = "none";
	button.style.cursor = "pointer";
	button.style.display = "flex";
	button.style.alignItems = "center";
	button.style.justifyContent = "center";
	button.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
	button.style.transition = "transform 0.2s ease, box-shadow 0.2s ease";
	button.style.background = config.primaryColor ?? "#0f172a";

	// Apply position
	const position = config.position ?? "bottom-right";
	if (position === "bottom-right") {
		button.style.bottom = "20px";
		button.style.right = "20px";
	} else if (position === "bottom-left") {
		button.style.bottom = "20px";
		button.style.left = "20px";
	} else if (position === "top-right") {
		button.style.top = "20px";
		button.style.right = "20px";
	} else if (position === "top-left") {
		button.style.top = "20px";
		button.style.left = "20px";
	}

	// Set icon
	button.innerHTML = FEEDBACK_ICON;

	// Hover effects
	button.addEventListener("mouseenter", () => {
		button.style.transform = "scale(1.05)";
		button.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.2)";
	});
	button.addEventListener("mouseleave", () => {
		button.style.transform = "scale(1)";
		button.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
	});

	// Click handler
	button.addEventListener("click", onClick);

	// Add to DOM
	document.body.appendChild(button);

	return {
		button,
		isOpen: false,
	};
}

/**
 * Update launcher to show open/close state
 * On mobile, hide the launcher when widget is open (widget has its own close button)
 */
export function updateLauncherState(
	state: LauncherState,
	isOpen: boolean,
	isMobile = false,
): void {
	state.isOpen = isOpen;
	state.button.innerHTML = isOpen ? CLOSE_ICON : FEEDBACK_ICON;
	state.button.setAttribute(
		"aria-label",
		isOpen ? "Close feedback widget" : "Open feedback widget",
	);

	// Hide launcher on mobile when widget is open (widget has close button in header)
	if (isMobile && isOpen) {
		state.button.style.display = "none";
	} else {
		state.button.style.display = "flex";
	}
}

/**
 * Destroy the launcher
 */
export function destroyLauncher(state: LauncherState): void {
	state.button.remove();
}
